import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ArticleContainer from '../../components/ArticleContainer';
import { useGetListArticleByFilter } from '../../hooks/useGetListArticleByFilter';
import GoBackButton from '../../components/GoBackButton';
import ExternalNewsCard from '../../components/DiscoveryComponent/MiddleContentComponent/components/external-news-card.component';
import { trendingTopicsApi } from '../../apis/trending-topics.api';
import SEO from '../../components/SEO';
import { generateSEOMetadata, collectionPages } from '../../utils/seo';

const RelatedArticlesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get('storyId');

  const [storyData, setStoryData] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState(null);

  const getTotalTopic = (amount) => {
    return amount;
  };

  const { loading, listArticle, total } = useGetListArticleByFilter({
    type: 'storyId',
    storyId: storyId
  });

  // Fetch story data when storyId is present
  useEffect(() => {
    if (storyId) {
      const fetchStoryData = async () => {
        try {
          setStoryLoading(true);
          setStoryError(null);

          const result = await trendingTopicsApi.getTrendingTopicById(storyId);

          if (result.success && result.data) {
            // Transform the story data to match the ExternalNewsCard format
            const transformedStory = transformTopicToNewsItem(result.data);
            setStoryData(transformedStory);
          } else {
            setStoryError(result.error || 'Failed to load story data');
            setStoryData(null);
          }
        } catch (error) {
          console.error('Error fetching story data:', error);
          setStoryError('Failed to load story data');
          setStoryData(null);
        } finally {
          setStoryLoading(false);
        }
      };

      fetchStoryData();
    }
  }, [storyId]);

  // Transform topic data to match ExternalNewsCard format
  const transformTopicToNewsItem = (topic) => {
    return {
      id: topic.id,
      title: topic.topic,
      description: topic.description || topic.topic,
      category: topic.category || 'General',
      timeAgo: 'Recently',
      sourceUrl: topic.sourceUrl || '#',
      urgencyIndicator: topic.urgencyIndicator || 'STABLE',
      readerDemand: topic.readerDemand || 'LOW',
      viewpoints: topic.viewpoints || [],
      tags: topic.tags || [],
      numberRelatedArticle: topic.numberRelatedArticle || 0
    };
  };

  if (!storyId) {
    return (
      <div className="w-full flex flex-start flex-col px-4 md:px-0 relative">
        <GoBackButton
          onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }}
        />
        <div className="text-center text-[20px] md:text-[32px] font-[800] text-growth-green-500 mt-10">
          Story ID is required
        </div>
      </div>
    );
  }

  // Generate SEO metadata for related articles page
  const seoMetadata = generateSEOMetadata.collectionPage(
    collectionPages['related-articles'].name,
    collectionPages['related-articles'].url,
    collectionPages['related-articles'].description
  );

  return (
    <div>
      <SEO {...seoMetadata} />
      <div className="w-full flex flex-start flex-col px-4 md:px-0 relative">
        <GoBackButton
          onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }}
        />
        <div className="flex sticky top-[68px] md:top-[80px] pt-3 md:pt-10 z-20 mx-[-16px] bg-white">
          <div className="w-full">
            <div className="text-center text-[20px] gap-2 md:text-[32px] font-[800] text-growth-green-500 flex items-center justify-center">
              Related Articles On Twygs
            </div>
            <p className="text-sm md:text-[24px] text-center font-[700] text-02-dim-gray mt-1 md:mt-2">
              {getTotalTopic(total)} Stories
            </p>
            <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-3">
              <p className="text-[18px] text-gray-600 font-[400] text-center">
                Curious about other topics?
              </p>
              <button
                className="whitespace-nowrap px-6 py-2 bg-growth-green-50 text-growth-green-900 font-[900] border-growth-green-100 border-[1px] rounded-lg flex items-center gap-2 hover:bg-growth-green-100 transition-colors"
                onClick={() => navigate('/?tab=newsHub')}
              >
                Check Our News Hub
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M7 17l9.2-9.2M17 17V7H7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-[604px] w-full mx-auto flex flex-col mt-10">
          {/* External News Card */}
          {storyLoading ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ) : storyError ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
              <div className="text-center text-red-500">{storyError}</div>
            </div>
          ) : storyData ? (
            <div className="mb-10">
              <ExternalNewsCard item={storyData} hideWriteButton={false} />
            </div>
          ) : null}

          {/* Articles List */}
          <ArticleContainer
            loading={loading}
            listArticle={listArticle ?? []}
            isHiddenFeaturedArticles
            isProfile
          />
        </div>
      </div>
    </div>
  );
};

export default RelatedArticlesPage;
